// ./src/utils/api/GloveboxDocumentService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { User } from "./UserService";
import { Car } from "./CarService";
import { GloveboxDocType } from "./AppEssentialsService";
import { UploadedFile } from "./UploadedFileService";

// =============================================================================
// GraphQL Fragments
// =============================================================================

const GLOVEBOX_DOC_SCALAR_FIELDS = `
  id
  accountId
  carId
  userId
  docTypeId
  customTypeName
  documentNumber
  issuedAt
  effectiveAt
  expiresAt
  inspectedAt
  issuingAuthority
  phone
  website
  cost
  costCurrency
  coverageAmount
  coverageCurrency
  remindBeforeDays
  notes
  uploadedFileId
  status
  version
  createdBy
  updatedBy
  createdAt
  updatedAt
  isExpired
  daysUntilExpiration
`;

const GLOVEBOX_DOC_REF_FIELDS = `
  docType {
    id
    code
    category
    name
    documentNumberLabel
  }
  car {
    id
    label
  }
  user {
    id
    avatarUrl
    firstName
    lastName
    email
  }
`;

const GLOVEBOX_DOC_FILE_FIELDS = `
  uploadedFile {
    id
    sizeBytes
    name
    fileUrl
    thumbnailUrl
    mimeType
  }
  uploadedFiles {
    id
    sizeBytes
    name
    fileUrl
    thumbnailUrl
    mimeType
  }
`;

// =============================================================================
// GraphQL Queries
// =============================================================================

const GLOVEBOX_DOCUMENT_LIST_QUERY = gql`
  query GloveboxDocumentList($filter: GloveboxDocumentFilter, $params: PaginationAndSorting) {
    gloveboxDocumentList(filter: $filter, params: $params) {
      code
      data {
        ${GLOVEBOX_DOC_SCALAR_FIELDS}
        ${GLOVEBOX_DOC_REF_FIELDS}
        uploadedFile {
          id
          sizeBytes
          name
          fileUrl
          thumbnailUrl
          mimeType
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const GLOVEBOX_DOCUMENT_GET_QUERY = gql`
  query GloveboxDocumentGet($id: ID) {
    gloveboxDocumentGet(id: $id) {
      code
      data {
        ${GLOVEBOX_DOC_SCALAR_FIELDS}
        docType {
          id
          code
          category
          name
          description
          documentNumberLabel
          hasDocumentNumber
          hasIssueDate
          hasEffectiveDate
          hasExpiration
          hasIssuingAuthority
          hasCost
          hasCoverageAmount
        }
        car {
          id
          label
        }
        user {
          id
          avatarUrl
          firstName
          lastName
          email
        }
        userCreated {
          id
          firstName
          lastName
        }
        userUpdated {
          id
          firstName
          lastName
        }
        ${GLOVEBOX_DOC_FILE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const GLOVEBOX_DOCUMENT_GET_MANY_QUERY = gql`
  query GloveboxDocumentGetMany($ids: [ID]) {
    gloveboxDocumentGetMany(ids: $ids) {
      code
      data {
        ${GLOVEBOX_DOC_SCALAR_FIELDS}
        ${GLOVEBOX_DOC_REF_FIELDS}
        ${GLOVEBOX_DOC_FILE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const GLOVEBOX_STATS_GET_QUERY = gql`
  query GloveboxStatsGet($withinDays: Int) {
    gloveboxStatsGet(withinDays: $withinDays) {
      code
      data {
        totalDocuments
        expiredDocuments
        expiringSoonDocuments
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// GraphQL Mutations
// =============================================================================

const GLOVEBOX_DOCUMENT_CREATE_MUTATION = gql`
  mutation GloveboxDocumentCreate($params: GloveboxDocumentInput) {
    gloveboxDocumentCreate(params: $params) {
      code
      data {
        ${GLOVEBOX_DOC_SCALAR_FIELDS}
        ${GLOVEBOX_DOC_REF_FIELDS}
        ${GLOVEBOX_DOC_FILE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const GLOVEBOX_DOCUMENT_UPDATE_MUTATION = gql`
  mutation GloveboxDocumentUpdate($where: GloveboxDocumentWhereInput, $params: GloveboxDocumentInput) {
    gloveboxDocumentUpdate(where: $where, params: $params) {
      code
      data {
        ${GLOVEBOX_DOC_SCALAR_FIELDS}
        ${GLOVEBOX_DOC_REF_FIELDS}
        ${GLOVEBOX_DOC_FILE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const GLOVEBOX_DOCUMENT_REMOVE_MUTATION = gql`
  mutation GloveboxDocumentRemove($where: GloveboxDocumentWhereInput) {
    gloveboxDocumentRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const GLOVEBOX_DOCUMENT_REMOVE_MANY_MUTATION = gql`
  mutation GloveboxDocumentRemoveMany($where: [GloveboxDocumentWhereInput]) {
    gloveboxDocumentRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

export interface GloveboxDocument {
  id?: string | undefined;
  accountId?: string | undefined;
  carId?: string | undefined;
  userId?: string | undefined;
  docTypeId?: number | undefined;
  customTypeName?: string | undefined;
  documentNumber?: string | undefined;
  issuedAt?: string | undefined;
  effectiveAt?: string | undefined;
  expiresAt?: string | undefined;
  inspectedAt?: string | undefined;
  issuingAuthority?: string | undefined;
  phone?: string | undefined | null;
  website?: string | undefined | null;
  cost?: number | undefined;
  costCurrency?: string | undefined;
  coverageAmount?: number | undefined;
  coverageCurrency?: string | undefined;
  remindBeforeDays?: number | undefined;
  notes?: string | undefined;
  uploadedFileId?: string | undefined;
  // Audit fields
  status?: number | undefined;
  version?: number | undefined;
  createdBy?: string | undefined;
  updatedBy?: string | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
  // Computed fields
  isExpired?: boolean | undefined;
  daysUntilExpiration?: number | undefined;
  // References
  docType?: GloveboxDocType | undefined;
  car?: Car | undefined;
  user?: User | undefined;
  userCreated?: User | undefined;
  userUpdated?: User | undefined;
  uploadedFile?: UploadedFile | undefined;
  uploadedFilesIds?: string[] | undefined;
  uploadedFiles?: UploadedFile[] | undefined;
}

export interface GloveboxDocumentInput {
  id?: string | null | undefined;
  carId?: string | null | undefined;
  userId?: string | null | undefined;
  docTypeId?: number | null | undefined;
  customTypeName?: string | null | undefined;
  documentNumber?: string | null | undefined;
  issuedAt?: string | null | undefined;
  effectiveAt?: string | null | undefined;
  expiresAt?: string | null | undefined;
  inspectedAt?: string | null | undefined;
  issuingAuthority?: string | null | undefined;
  phone?: string | null | undefined;
  website?: string | null | undefined;
  cost?: number | null | undefined;
  costCurrency?: string | null | undefined;
  coverageAmount?: number | null | undefined;
  coverageCurrency?: string | null | undefined;
  remindBeforeDays?: number | null | undefined;
  notes?: string | null | undefined;
  uploadedFileId?: string | null | undefined;
  uploadedFilesIds?: string[] | null | undefined;
  status?: number | null | undefined;
}

export interface GloveboxDocumentFilter {
  id?: string[] | null | undefined;
  accountId?: string[] | null | undefined;
  userId?: string[] | null | undefined;
  carId?: string[] | null | undefined;
  docTypeId?: number[] | null | undefined;
  category?: string[] | null | undefined;
  expiresAtFrom?: string | null | undefined;
  expiresAtTo?: string | null | undefined;
  isExpired?: boolean | null | undefined;
  expiresWithinDays?: number | null | undefined;
  status?: number[] | null | undefined;
  searchKeyword?: string | null | undefined;
  expiredOrExpiring?: boolean | null | undefined; // return exppired record or those that are expiring within N days as per user preferences
}

export interface GloveboxDocumentWhereInput {
  id?: string | undefined;
}

export interface GloveboxStats {
  totalDocuments?: number | undefined;
  expiredDocuments?: number | undefined;
  expiringSoonDocuments?: number | undefined;
}

// =============================================================================
// Service Class
// =============================================================================

/**
 * Glovebox Document service that handles all glovebox document-related API calls.
 * This is a service for managing vehicle and driver documents (insurance, registration, licenses, etc.)
 * Supports CRUD operations: list, get, getMany, create, update, remove, removeMany
 * Also provides stats for dashboard display.
 */
export class GloveboxDocumentService {
  /**
   * Get a list of glovebox documents with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: GloveboxDocumentFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(GLOVEBOX_DOCUMENT_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single glovebox document by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(GLOVEBOX_DOCUMENT_GET_QUERY, {
      id,
    });
    return result;
  }

  /**
   * Get multiple glovebox documents by IDs
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(GLOVEBOX_DOCUMENT_GET_MANY_QUERY, {
      ids,
    });
    return result;
  }

  /**
   * Get glovebox statistics for dashboard
   * @param withinDays Number of days to consider for "expiring soon" count (default: 30)
   */
  static async getStats(withinDays?: number): Promise<OpResult> {
    const result = await ApiService.executeQuery(GLOVEBOX_STATS_GET_QUERY, {
      withinDays: withinDays || 30,
    });
    return result;
  }

  /**
   * Create a new glovebox document
   */
  static async create(params: GloveboxDocumentInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(GLOVEBOX_DOCUMENT_CREATE_MUTATION, {
      params,
    });
    return result;
  }

  /**
   * Update an existing glovebox document
   */
  static async update(
    where: GloveboxDocumentWhereInput,
    params: GloveboxDocumentInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(GLOVEBOX_DOCUMENT_UPDATE_MUTATION, {
      where,
      params,
    });
    return result;
  }

  /**
   * Remove/delete a glovebox document (soft delete)
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(GLOVEBOX_DOCUMENT_REMOVE_MUTATION, {
      where: { id },
    });
    return result;
  }

  /**
   * Remove/delete multiple glovebox documents (soft delete)
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      GLOVEBOX_DOCUMENT_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }
}